import React, { FC, useState, ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, FormControl, Grid, TextField, Box } from '@mui/material';
import { getLabsTemplate } from 'apollo/templates/user/query.graphql';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import DateFromToFilter from 'components/shared/date-from-to-filter/date-from-to-filter.component';
import { DateRange } from '@mui/lab';
import { OrderOnDateFilter } from 'models/order';
import OrderReviewSelectFilter from 'components/order-review/order-review-filter/order-review-select-filter/order-review-select-filter.component';
import MultiSelectFilter from 'components/shared/multi-select-filter/multi-select-filter.component';
import { TransportFilter, TransportOnDate, TransportStatus } from 'models/transport';

type TransportOrdersPageActionsProps = {
    onFilterChange: (filters: TransportFilter) => void;
};
const TransportOrdersPageActions: FC<TransportOrdersPageActionsProps> = ({
    onFilterChange,
}: TransportOrdersPageActionsProps) => {
    const { t: translate } = useTranslation(['transport', 'common']);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState('');
    const [searchOpterIdTimeout, setSearchOpterIdTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [searchOpterId, setSearchOpterId] = useState('');
    const [status, setStatus] = useState<string[]>([]);
    const [labId, setLabId] = useState('');
    const [dateFilterType, setDateFilterType] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const [dateFromTo, setDateFromTo] = useState<DateRange<Date>>([null, null] as DateRange<Date>);

    const {
        data: { labs } = {},
        loading,
        error,
    } = useQuery(getLabsTemplate, {
        variables: {
            isTransportEnable: true,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleLabChange = (_: React.SyntheticEvent, lab: Organization | null) => {
        const value = lab ? lab._id : '';
        setLabId(value);
        handleChange(search, searchOpterId, value, dateFilterType, dateFromTo, status);
    };

    const handleDateChange = (date: DateRange<Date>) => {
        setDateFromTo(date);
        if (date[0] !== null && date[1] !== null) {
            handleChange(search, searchOpterId, labId, dateFilterType, date, status);
        }
    };

    const handleTransportOnDateSelectChange = (dateFilterType: string) => {
        setDateFilterType(dateFilterType);
        if (dateFilterType === TransportOnDate.NO_DATE_FILTER) {
            setDateFromTo([null, null]);
            handleChange(search, searchOpterId, labId, dateFilterType, dateFromTo, status);
        }
    };

    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 3) {
                handleChange(value, searchOpterId, labId, dateFilterType, dateFromTo, status);
            }
        }, 300);
        setSearchTimeout(timeout);
    };

    const handleTransportStatusSelectChange = (status: string[]) => {
        setStatus(status.length ? status : []);
        handleChange(search, searchOpterId, labId, dateFilterType, dateFromTo, status);
    };

    const handleSearchOpterIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchOpterId(value);
        if (searchOpterIdTimeout) {
            clearTimeout(searchOpterIdTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 5) {
                handleChange(search, value, labId, dateFilterType, dateFromTo, status);
            }
        }, 300);
        setSearchOpterIdTimeout(timeout);
    };

    const handleChange = (
        search: string,
        searchOpterId: string,
        labId: string,
        dateFilterType: string,
        dateFromTo: DateRange<Date>,
        status: string[],
    ) => {
        onFilterChange({
            search,
            searchExternalReference: searchOpterId ? searchOpterId : undefined,
            labId,
            dateFilterType: dateFilterType !== TransportOnDate.NO_DATE_FILTER ? dateFilterType : undefined,
            dateFromTo: dateFilterType !== TransportOnDate.NO_DATE_FILTER ? dateFromTo : undefined,
            status: status.length ? status : undefined,
        });
    };

    return (
        <Box mb={1} display="flex" alignItems="center">
            <Grid container spacing={1}>
                <Grid item md={2} sm={6} xs={12}>
                    <TextField
                        fullWidth
                        onChange={handleSearchTextChange}
                        name="search"
                        size="small"
                        label={translate('transport:FREE_TEXT_FILTER')}
                        placeholder={translate('transport:FREE_TEXT_FILTER_PLACEHOLDER')}
                    />
                </Grid>
                <Grid item md={1} sm={6} xs={12}>
                    <TextField
                        id="opter-id"
                        fullWidth
                        label={translate('transport:OPTER_ID')}
                        placeholder={translate('transport:OPTER_ID')}
                        onChange={handleSearchOpterIdChange}
                    />
                </Grid>
                <Grid item md={2} sm={6} xs={12}>
                    <FormControl fullWidth size="small">
                        <Autocomplete
                            id="labs"
                            disabled={loading}
                            options={labs || []}
                            onChange={handleLabChange}
                            getOptionLabel={(option: Organization) => option.name}
                            renderInput={(params) => (
                                <TextField {...params} size="small" label={translate('common:LAB')} />
                            )}
                        />
                    </FormControl>
                </Grid>
                <Grid item md={2} sm={6} xs={12}>
                    <OrderReviewSelectFilter
                        label={translate('transport:TRANSPORT_DATE_FILTER')}
                        optionSelected={dateFilterType || TransportOnDate.NO_DATE_FILTER}
                        options={Object.values(TransportOnDate)}
                        onOptionChange={handleTransportOnDateSelectChange}
                        translationNamespace="transport"
                    />
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                    <DateFromToFilter
                        dateFromTo={dateFromTo || [null, null]}
                        dateFilterType={dateFilterType || OrderOnDateFilter.NO_DATE_FILTER}
                        onDateChange={handleDateChange}
                    />
                </Grid>
                <Grid item md={2} sm={6} xs={12}>
                    <MultiSelectFilter
                        optionSelected={status || []}
                        label={translate('transport:TRANSPORT_STATUS')}
                        options={Object.values(TransportStatus)}
                        onOptionChange={handleTransportStatusSelectChange}
                        translationNamespace="transport"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default TransportOrdersPageActions;
